import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://api.mayorsoftware.ch/v1";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error.response?.data?.error || error.message || "An error occurred";

    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
      return Promise.reject(error);
    }

    toast.error(message);
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  register: async (userData) => {
    const { data } = await api.post("/auth/register", userData);
    return data;
  },

  login: async (credentials) => {
    const { data } = await api.post("/auth/login", credentials);
    return data;
  },

  getProfile: async () => {
    const { data } = await api.get("/auth/profile");
    return data;
  },

  changePassword: async (passwordData) => {
    const { data } = await api.post("/auth/change-password", passwordData);
    return data;
  },

  forgotPassword: async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  },

  resetPassword: async (tokenData) => {
    const { data } = await api.post("/auth/reset-password", tokenData);
    return data;
  },

  verifyEmail: async (token) => {
    const { data } = await api.post("/auth/verify-email", { token });
    return data;
  },

  resendVerification: async (email) => {
    const { data } = await api.post("/auth/resend-verification", { email });
    return data;
  },

  twoFactorValidate: async (tempToken, code) => {
    const { data } = await api.post("/auth/2fa/validate", { tempToken, code });
    return data;
  },

  twoFactorSetup: async () => {
    const { data } = await api.post("/auth/2fa/setup");
    return data;
  },

  twoFactorEnable: async (code) => {
    const { data } = await api.post("/auth/2fa/enable", { code });
    return data;
  },

  twoFactorDisable: async (password) => {
    const { data } = await api.post("/auth/2fa/disable", { password });
    return data;
  },
};

// Tokens API
export const tokensAPI = {
  list: async () => {
    const { data } = await api.get("/tokens");
    return data;
  },

  create: async (tokenData) => {
    const { data } = await api.post("/tokens", tokenData);
    return data;
  },

  update: async (tokenId, updates) => {
    const { data } = await api.put(`/tokens/${tokenId}`, updates);
    return data;
  },

  delete: async (tokenId) => {
    const { data } = await api.delete(`/tokens/${tokenId}`);
    return data;
  },

  getUsage: async (tokenId, days = 30) => {
    const { data } = await api.get(`/tokens/${tokenId}/usage?days=${days}`);
    return data;
  },
};

// Usage API - Updated to use new token-balance endpoints
export const usageAPI = {
  getHistory: async (params = {}) => {
    // Filter out empty string values to avoid validation errors
    const filteredParams = Object.entries(params).reduce(
      (acc, [key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    // Map parameters to usage endpoint format
    const mappedParams = {
      page: filteredParams.page || 1,
      limit: filteredParams.limit || 20,
      serviceType: filteredParams.serviceType,
      tokenId: filteredParams.tokenId,
      startDate: filteredParams.startDate,
      endDate: filteredParams.endDate,
    };

    const queryString = new URLSearchParams(
      Object.entries(mappedParams).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {}),
    ).toString();

    // Use usage/history endpoint which returns UsageEvents with totalCost in CHF
    const { data } = await api.get(
      `/usage/history${queryString ? `?${queryString}` : ""}`,
    );

    // Data is already in the correct format from UsageEvents
    return {
      usage: data.usage,
      pagination: data.pagination,
      summary: data.summary,
    };
  },

  getAnalytics: async (periodOrOptions = "month", days = 30) => {
    const options =
      periodOrOptions && typeof periodOrOptions === "object"
        ? periodOrOptions
        : { period: periodOrOptions, days };

    const mappedParams = {
      limit: options.limit || 100,
      serviceType: options.serviceType,
      tokenId: options.tokenId,
      startDate: options.startDate,
      endDate: options.endDate,
    };

    const queryString = new URLSearchParams(
      Object.entries(mappedParams).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {}),
    ).toString();

    // Query UsageEvents directly from /usage/history with max allowed limit
    const { data } = await api.get(
      `/usage/history${queryString ? `?${queryString}` : ""}`,
    );

    // Aggregate by service type from UsageEvents (which have correct pricing)
    const serviceMap = {};
    const dayMap = {};

    if (data.usage && data.usage.length > 0) {
      data.usage.forEach((event) => {
        const serviceType = event.serviceType || "unknown";
        const audioSeconds = event.audioSeconds || 0;
        const cost = event.totalCost || 0; // Use the pre-calculated cost from backend

        // Aggregate by service
        if (!serviceMap[serviceType]) {
          serviceMap[serviceType] = { events: 0, audioSeconds: 0, cost: 0 };
        }
        serviceMap[serviceType].events += 1;
        serviceMap[serviceType].audioSeconds += audioSeconds;
        serviceMap[serviceType].cost += cost;

        // Aggregate by day
        const date = new Date(event.createdAt);
        const dateKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1,
        ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        if (!dayMap[dateKey]) {
          dayMap[dateKey] = {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
            events: 0,
            audioSeconds: 0,
            cost: 0,
          };
        }
        dayMap[dateKey].events += 1;
        dayMap[dateKey].audioSeconds += audioSeconds;
        dayMap[dateKey].cost += cost;
      });
    }

    // Convert to arrays
    const serviceBreakdown = Object.entries(serviceMap).map(
      ([serviceType, stats]) => ({
        serviceType,
        events: stats.events,
        audioSeconds: stats.audioSeconds,
        cost: stats.cost,
      }),
    );

    const timeSeries = Object.values(dayMap)
      .sort((a, b) => {
        return (
          new Date(a.year, a.month - 1, a.day) -
          new Date(b.year, b.month - 1, b.day)
        );
      })
      .map((day) => ({
        period: { year: day.year, month: day.month, day: day.day },
        events: day.events,
        audioSeconds: day.audioSeconds,
        cost: day.cost,
      }));

    return {
      period: options.period || "month",
      days: parseInt(options.days || days),
      serviceBreakdown,
      timeSeries,
      totals: {
        totalCost: serviceBreakdown.reduce((sum, s) => sum + s.cost, 0),
        totalSeconds: serviceBreakdown.reduce(
          (sum, s) => sum + s.audioSeconds,
          0,
        ),
        totalEvents: serviceBreakdown.reduce((sum, s) => sum + s.events, 0),
      },
      chartData: timeSeries.map((ts) => ({
        date: `${ts.period.year}-${String(ts.period.month).padStart(
          2,
          "0",
        )}-${String(ts.period.day).padStart(2, "0")}`,
        events: ts.events,
        audioSeconds: ts.audioSeconds,
        cost: ts.cost,
      })),
    };
  },
};

// User API
export const userAPI = {
  getProfile: async () => {
    const { data } = await api.get("/users/profile");
    return data;
  },

  updateProfile: async (profileData) => {
    const { data } = await api.put("/users/profile", profileData);
    return data;
  },

  addBalance: async (amount, paymentMethodId) => {
    const { data } = await api.post("/users/add-balance", {
      amount,
      paymentMethodId,
    });
    return data;
  },

  getBalance: async () => {
    const { data } = await api.get("/users/balance");
    return data;
  },
};

// Billing API
export const billingAPI = {
  getInvoices: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const { data } = await api.get(
      `/billing/invoices${queryString ? `?${queryString}` : ""}`,
    );
    return data;
  },

  getInvoiceDetails: async (invoiceId) => {
    const { data } = await api.get(`/billing/invoices/${invoiceId}`);
    return data;
  },

  downloadInvoicePDF: async (invoiceId) => {
    const response = await api.get(`/billing/invoices/${invoiceId}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  },

  resendInvoiceEmail: async (invoiceId) => {
    const { data } = await api.post(`/billing/invoices/${invoiceId}/send`);
    return data;
  },

  generateInvoice: async (periodData) => {
    const { data } = await api.post("/billing/generate-invoice", periodData);
    return data;
  },

  resetUsageEvents: async () => {
    const { data } = await api.post("/billing/reset-usage-events");
    return data;
  },

  getSummary: async (months = 6) => {
    const { data } = await api.get(`/billing/summary?months=${months}`);
    return data;
  },

  getRates: async () => {
    const { data } = await api.get("/billing/rates");
    return data;
  },

  getAllUsersConsumption: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const { data } = await api.get(
      `/billing/admin/all-users-consumption${queryString ? `?${queryString}` : ""}`,
    );
    return data;
  },

  generateInvoiceForUser: async (userId, periodData = {}) => {
    const { data } = await api.post(
      `/billing/admin/generate-invoice/${userId}`,
      periodData,
    );
    return data;
  },

  getUserInvoices: async (userId) => {
    const { data } = await api.get(`/billing/admin/user-invoices/${userId}`);
    return data;
  },

  updateInvoiceStatus: async (invoiceId, status) => {
    const { data } = await api.put(`/billing/admin/invoices/${invoiceId}`, {
      status,
    });
    return data;
  },
};

// Admin API
export const adminAPI = {
  createInvite: async ({ email, note, expiresAt, lang } = {}) => {
    const payload = {};
    if (email) payload.email = email;
    if (note) payload.note = note;
    if (expiresAt) payload.expiresAt = expiresAt;
    if (lang) payload.lang = lang;
    const { data } = await api.post("/admin/invites", payload);
    return data;
  },

  listInvites: async () => {
    const { data } = await api.get("/admin/invites");
    return data;
  },

  revokeInvite: async (id) => {
    const { data } = await api.delete(`/admin/invites/${id}`);
    return data;
  },

  listUserTokens: async (email) => {
    const { data } = await api.get(
      `/admin/tokens?email=${encodeURIComponent(email)}`,
    );
    return data;
  },

  listAllTokens: async ({ search = "", page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.set("search", search);
    const { data } = await api.get(`/admin/tokens?${params.toString()}`);
    return data;
  },

  revokeUserToken: async (tokenId) => {
    const { data } = await api.delete(`/admin/tokens/${tokenId}`);
    return data;
  },
};

export default api;
